import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import SEO from "@/components/SEO";
import { SubpageNav, SubpageBackLink, SubpageFooter } from "@/components/SubpageLayout";
import SubpageHero from "@/components/SubpageHero";
import { Card, CardContent } from "@/components/ui/card";
import {
  BookOpen, ChevronDown, ChevronUp, UserPlus, ClipboardList, LayoutDashboard,
  Apple, Brain, Zap, Dna, FlaskConical, Calculator, FileText,
  CreditCard, ArrowRight, ScanBarcode, BarChart3, Heart, Target,
} from "lucide-react";

const STEP_ICON_MAP = {
  UserPlus, ClipboardList, LayoutDashboard, Apple, Brain, Zap,
  Dna, FlaskConical, Calculator, FileText, CreditCard, ScanBarcode,
  BarChart3, Heart, Target,
};

const GUIDE_BG = {
  title: "Ръководство за потребителя",
  subtitle: "Научете как да извлечете максимума от Nutramilo — стъпка по стъпка",
  steps: [
    {
      id: "start",
      icon: "UserPlus",
      color: "#0EA5E9",
      title: "1. Регистрация и първи стъпки",
      summary: "Създайте акаунт и попълнете здравния си профил за персонализирани препоръки.",
      details: [
        `Отворете Nutramilo и натиснете "Регистрация". Въведете име, имейл и парола.`,
        `Ако сте лекар или диетолог, изберете таба "Лекар" и качете CV и диплома за верификация.`,
        "След регистрация ще бъдете насочени към метаболитна оценка — кратък въпросник за вашето здраве, цели и начин на живот.",
        "На базата на отговорите Ви платформата определя Вашия метаболитен профил и изчислява личен дневен инсулинов бюджет.",
        "Можете да промените профила си по всяко време от настройките.",
      ],
    },
    {
      id: "dashboard",
      icon: "LayoutDashboard",
      color: "#6366F1",
      title: "2. Дашборд — вашият център за управление",
      summary: "Всичко на едно място — хранене, анализи, AI помощници и проследяване.",
      details: [
        "Горната лента показва четири показателя: Общ резултат, Хранителен баланс, Метаболитна активност и AI препоръки.",
        "Лявата колонка е за хранене — дневник, планировчик, рецепти и метаболитен панел.",
        "Дясната колонка е за анализи — AI чат, клиничен анализ, нутригеномика и седмичен отчет.",
        "На мобилен телефон използвайте долните табове: Хранене, Анализи, Още.",
      ],
    },
    {
      id: "meal-diary",
      icon: "Apple",
      color: "#10B981",
      title: "3. Хранителен дневник",
      summary: "Записвайте какво ядете и вижте как храната влияе на инсулиновото ви натоварване.",
      details: [
        `Натиснете "+" до хранене (Закуска, Обяд, Вечеря) и потърсете храна по име.`,
        "Платформата показва калории, белтъчини, въглехидрати, мазнини и инсулинов товар за всяка храна.",
        `Можете да сканирате баркод на продукт — отидете в "Още" на мобилен или скролнете надолу на компютър.`,
        "Дневното проследяване показва общо калории, макроси, фибри и 16 микронутриента.",
        "Всеки ден получавате оценка на инсулиновия товар — колкото по-ниска, толкова по-добре управлявате натоварването.",
      ],
    },
    {
      id: "ai-chat",
      icon: "Brain",
      color: "#8B5CF6",
      title: "4. AI помощници",
      summary: "Четири специализирани AI помощника отговарят на вашите въпроси за хранене и здраве.",
      details: [
        `Въведете въпрос в полето "Попитай AI" — например: "Какво да ям за закуска при инсулинова резистентност?"`,
        "Системата автоматично избира най-подходящия помощник: EndoBot (метаболизъм), NutriBot (хранене), NephroBot (бъбреци) или HemoBot (кръв).",
        "AI помощниците имат достъп до вашия профил, лабораторни данни и генетична информация (ако е качена).",
        "Отговорът е персонализиран — не е общ съвет, а препоръка на базата на конкретните ви данни.",
        "Безплатен план: 1 въпрос/ден. Core: 40/месец. Pro: 120/месец. Clinic: 1000/месец.",
      ],
    },
    {
      id: "dil-panel",
      icon: "Zap",
      color: "#F59E0B",
      title: "5. Метаболитен панел и инсулинов товар",
      summary: "Вижте вашия инсулинов бюджет, фенотип и ежедневен прогрес.",
      details: [
        "Дневният инсулинов бюджет е лична стойност, която показва какво натоварване е подходящо за Вас.",
        "Метаболитният фенотип (P1 до P5) определя вашата хранителна стратегия — от фокус върху въглехидратите до адренален баланс.",
        "Панелът показва колко от дневния бюджет сте използвали, целевите калории, разпределението на хранителните вещества и показателите за Вашия профил.",
        "Цветовият индикатор показва дали сте в зелена (добра), жълта (внимание) или червена (над лимита) зона.",
      ],
    },
    {
      id: "meal-planner",
      icon: "Target",
      color: "#EC4899",
      title: "6. Хранителен планировчик",
      summary: "AI създава 7-дневен хранителен план, съобразен с вашия профил.",
      details: [
        `Натиснете "Генерирай план" и AI ще създаде седмично меню с храни от три бази данни.`,
        "Всяка храна показва калории, протеин, мазнини, въглехидрати, фибри и оценка за качество.",
        "Можете да добавите цял ден или цялата седмица в хранителния дневник с едно натискане.",
        "Седмичният преглед показва графики за калориите, инсулиновия товар и хранителния баланс.",
        "Генерирането на план консумира 1 AI анализ от месечния ви лимит.",
      ],
    },
    {
      id: "plate-simulator",
      icon: "ScanBarcode",
      color: "#0EA5E9",
      title: "7. Симулатор на чинията и разпознаване по снимка",
      summary: "Симулирай чиния и виж индексите си преди да ядеш — или просто снимай.",
      details: [
        "Симулатор на чинията: добавяте храни от базата, чрез баркод или на ръка и виждате в реално време възпалителното натоварване, инсулиновия товар, баланса на мастните киселини и киселинното натоварване — още преди да сте хапнали.",
        `AI Auto-Fix: когато някой индекс е извън нормата (UIS > 60%, ω > 10:1 и т.н.), системата автоматично предлага конкретни замени от 23,600 USDA храни.`,
        `Multi-Bot AI обяснение: EndoBot + NephroBot + NutriBot работят паралелно за 14-секционен клиничен анализ на чинията.`,
        "Разпознаване по снимка: снимате чинията, съставките се разпознават за секунди и се създава готов запис в дневника.",
        `Recipe URL Import: вкарай линк от NYT Cooking, AllRecipes, BBC Good Food или 580+ други сайта — автоматично импортиране.`,
        "Готварска книга: запазените рецепти пазят вече изчислените показатели, с филтри за противовъзпалително хранене и нисък инсулинов товар.",
      ],
    },
    {
      id: "vais",
      icon: "Heart",
      color: "#DC2626",
      title: "8. Висцерален мастен индекс и дигитални близнаци",
      summary: "Висцерална адипозност без скенер. Прогноза на тялото при промяна на хранене.",
      details: [
        "Висцералният мастен индекс е оценка от 0 до 100 за вътрешната коремна мазнина, без нужда от скенер. Изчислява се от телесните измервания, лабораторните показатели, признаците на възпаление, начина на живот и възрастта, като водещо е отношението талия към ръст.",
        "Биологична възраст (Levine PhenoAge) от лабораторни данни.",
        "Дигиталните близнаци показват как биха се променили показателите Ви при по-нисък инсулинов товар, друго тегло или промяна в начина на живот.",
        "Safety-check: при високорискови популации (диализа, активна онкология) системата препоръчва специалист.",
      ],
    },
    {
      id: "nutrigenomics",
      icon: "Dna",
      color: "#06B6D4",
      title: "9. Нутригеномика",
      summary: "Качете ДНК файл и разберете как гените ви влияят на нуждите от хранителни вещества.",
      details: [
        "Поддържаме файлове от 23andMe и AncestryDNA (формат TSV/TXT).",
        "Системата анализира 25 гена и 37 генетични варианта, свързани с витамини, минерали и метаболизъм.",
        `За всеки ген виждате ниво на риск (нисък, умерен, висок) и конкретна препоръка — например "Витамин D3: 4000-5000 IU/ден".`,
        "Генетичният доклад може да се изтегли като PDF за споделяне с лекар.",
        "Генетичните данни автоматично се включват в AI препоръките и клиничния анализ.",
      ],
    },
    {
      id: "clinical",
      icon: "FlaskConical",
      color: "#EF4444",
      title: "10. Клиничен анализ",
      summary: "Подробен анализ на вашия метаболитен статус на базата на профил, лабораторни и генетични данни.",
      details: [
        "Клиничният анализ разпознава 10 метаболитни модела: инсулинова резистентност, щитовидна жлеза, надбъбречни жлези, сърдечно-съдов риск и други.",
        "Данните идват от три източника: здравен профил, лабораторни изследвания и генетичен анализ (ако е наличен).",
        "Опростеният изглед (за пациенти) показва ключови области с ясни препоръки — без медицинска терминология.",
        "Подробният изглед (за лекари) показва пълни метаболитни модели, механизми и научни данни.",
        "След анализа получавате предложения какво да допълните в ежедневното проследяване.",
        "AI анализ от екип специалисти (Refinement) консумира 1 AI анализ от лимита и дава по-задълбочен преглед.",
      ],
    },
    {
      id: "calculators",
      icon: "Calculator",
      color: "#0EA5E9",
      title: "11. Клинични калкулатори",
      summary: "Единадесет валидирани клинични калкулатора за оценка на различни аспекти на здравето.",
      details: [
        "BMI — индекс на телесна маса с категории.",
        "eGFR (CKD-EPI 2021) — оценка на бъбречната функция.",
        "HOMA-IR — инсулинова чувствителност на базата на глюкоза и инсулин на гладно.",
        "Сърдечно-съдов риск (Framingham) — 10-годишен риск на базата на възраст, холестерол и кръвно налягане.",
        "Диабет (FINDRISC) — оценка на риска от диабет тип 2.",
        "Витамин D — препоръчителна дневна доза на базата на теглото и нивото.",
        "Прием на вода — дневна нужда от течности.",
        "Менструален цикъл — проследяване на фазите и корекция на дневния бюджет.",
        "Фибри и сън — оценка на приема и качеството.",
        "Риск от рак на гърдата — Gail/BCRAT + българска GLOBOCAN 2024 статистика.",
        "Допълнително: биологична възраст (Levine PhenoAge) и мамографска плътност (BI-RADS) скрининг на изображение.",
        "Всички резултати се записват и са достъпни за AI помощниците. Могат да се изтеглят като PDF доклад.",
      ],
    },
    {
      id: "weekly",
      icon: "BarChart3",
      color: "#10B981",
      title: "12. Седмичен AI отчет",
      summary: "Всяка седмица получавате AI анализ на прогреса с оценка и препоръки.",
      details: [
        "Отчетът обхваща храненето Ви, развитието на инсулиновия товар, приема на фибри, хранителната плътност и ситостта.",
        "Показва постижения (какво сте направили добре) и области за подобрение.",
        "Включва сравнение с предходната седмица и конкретни стъпки за следващата.",
        "Ако имате клиничен анализ, отчетът включва и данни от метаболитните ви модели.",
        "Генерирането на отчет е безплатно за Core и Pro потребители.",
      ],
    },
    {
      id: "plans",
      icon: "CreditCard",
      color: "#F59E0B",
      title: "13. Планове и AI анализи",
      summary: "Как работи месечният лимит AI анализи и какво включва всеки план.",
      details: [
        "Всяко действие — клиничен преглед, хранителен план, отчет или въпрос — използва един анализ от месечния Ви лимит. Без скрити изчисления.",
        "БЕЗПЛАТЕН (0 €): дневник на храненето, инсулинов товар, единадесет калкулатора, библиотека и един въпрос на ден.",
        "PRO (от 19.90 € на месец): 120 анализа, висцерален мастен индекс, разчитане на изследвания, дигитални близнаци и отчети за принтиране.",
        "КЛИНИКА (от 49 € на месец): за медицински специалисти — пациентски профили, клинични бележки и отчети с логото на практиката.",
        "КЛИНИКА ПЪЛЕН (99 € на месец): до двадесет и пет активни пациенти, клинични протоколи и отчети с логото на практиката.",
        "Корпоративни условия от 499 € на месец — по заявка.",
        "Цените са съобразени с пазара, на който се намирате. При годишно плащане получавате отстъпка.",
        "Не сте готови за абонамент? Можете да отключите единичен анализ.",
      ],
    },
  ],
};

const GUIDE_EN = {
  title: "User Guide",
  subtitle: "Learn how to get the most out of Nutramilo — step by step",
  steps: [
    { id: "start", icon: "UserPlus", color: "#0EA5E9", title: "1. Registration & first steps", summary: "Create an account and fill in your health profile for personalized recommendations.", details: ["Open Nutramilo and click 'Register'. Enter name, email, and password.", "If you're a doctor or dietitian, select the 'Clinician' tab and upload CV and diploma.", "After registration, you'll be guided through a metabolic assessment — a short questionnaire.", "Based on your answers, the system determines your metabolic phenotype (P1-P5) and creates a personal insulin budget (DIL)."] },
    { id: "dashboard", icon: "LayoutDashboard", color: "#6366F1", title: "2. Dashboard — your control center", summary: "Everything in one place — meals, analyses, AI assistants, and tracking.", details: ["Top bar shows four indicators: Overall Score, Nutritional Balance, Metabolic Activity, AI Recommendations.", "Left column is for meals — diary, planner, recipes, metabolic panel.", "Right column is for analyses — AI chat, clinical analysis, nutrigenomics, weekly report.", "On mobile, use bottom tabs: Meals, Analyses, More."] },
    { id: "meal-diary", icon: "Apple", color: "#10B981", title: "3. Meal diary", summary: "Log what you eat and see how food affects your insulin load.", details: ["Tap '+' next to a meal (Breakfast, Lunch, Dinner) and search for food by name.", "The system shows calories, macros, and DIL value for each food.", "You can scan product barcodes — go to 'More' on mobile or scroll down on desktop.", "Daily tracking shows total calories, macros, fiber, and 16 micronutrients."] },
    { id: "ai-chat", icon: "Brain", color: "#8B5CF6", title: "4. AI assistants", summary: "Four specialized AI assistants answer your nutrition and health questions.", details: ["Type a question in 'Ask AI' — e.g., 'What should I eat for breakfast with insulin resistance?'", "The system automatically selects the best assistant: EndoBot, NutriBot, NephroBot, or HemoBot.", "AI assistants have access to your profile, lab data, and genetic information.", "Free: 1 Q/day. Core: 40/month. Pro: 120/month. Clinic: 1000/month."] },
    { id: "dil-panel", icon: "Zap", color: "#F59E0B", title: "5. Metabolic panel & DIL", summary: "See your insulin budget, phenotype, and daily progress.", details: ["DIL is a personalized value showing your ideal daily insulin load.", "Your metabolic phenotype (P1-P5) determines your nutritional strategy.", "The panel shows daily DIL usage, target calories, and macro distribution."] },
    { id: "meal-planner", icon: "Target", color: "#EC4899", title: "6. Meal planner", summary: "AI creates a 7-day meal plan tailored to your profile.", details: ["Click 'Generate Plan' and AI creates a weekly menu from three food databases.", "Each food shows calories, macros, fiber, and quality score.", "Add a full day or week to your diary with one click. Consumes 1 AI analysis from your monthly limit."] },
    { id: "plate-simulator", icon: "ScanBarcode", color: "#0EA5E9", title: "7. Симулатор на чинията и разпознаване по снимка", summary: "Simulate a plate and see indices before you eat — or just photograph it.", details: ["Plate Simulator: add foods (USDA, barcode, manual) and instantly see UIS, DIL, NIS, ω-6:ω-3 balance, NEAP — before even eating.", "AI Auto-Fix: when an index is out of range (UIS > 60%, ω > 10:1, etc.), the system proposes concrete swaps from 23,600 USDA foods.", "Multi-Bot AI explanation: EndoBot + NephroBot + NutriBot run in parallel for a 14-section clinical analysis of the plate.", "Recipe Vision: photograph a meal, AI recognises ingredients in 3 seconds (1 AI analysis).", "Recipe URL Import: paste a link from NYT Cooking, AllRecipes, BBC Good Food or 580+ other sites — automatic import.", "Recipe Vault (Готварска книга): saved recipes with cached UIS/DIL/NIS/ω scores. 6 filters + 5 sort options."] },
    { id: "vais", icon: "Heart", color: "#DC2626", title: "8. Висцерален мастен индекс и дигитални близнаци", summary: "Visceral adiposity without imaging. Forecast your body under different dietary scenarios.", details: ["VAIS (Visceral Adiposity Intelligence Stack) — deterministic 0-100 score for visceral fat (no MRI/CT). 5 domains (anthropometry, metabolic markers, inflammation, lifestyle, demographics), WHtR as primary driver.", "Biological Age (Levine PhenoAge) from lab data.", "Digital Twin Horizons projects how your indices change under different DIL targets, weight, or lifestyle scenarios.", "Safety-check: for high-risk populations (dialysis, active oncology) the system recommends a specialist."] },
    { id: "nutrigenomics", icon: "Dna", color: "#06B6D4", title: "9. Nutrigenomics", summary: "Upload a DNA file to understand how your genes affect nutrient needs.", details: ["We support 23andMe and AncestryDNA files (TSV/TXT format).", "The system analyzes 25 genes and 37 variants related to vitamins, minerals, and metabolism.", "For each gene you see risk level and specific recommendation.", "Genetic report downloadable as PDF."] },
    { id: "clinical", icon: "FlaskConical", color: "#EF4444", title: "10. Clinical analysis", summary: "Detailed analysis of your metabolic status from profile, lab, and genetic data.", details: ["Identifies 10 metabolic patterns: insulin resistance, thyroid, adrenal, cardiovascular risk, etc.", "Simplified view (patients): clear recommendations without medical jargon.", "Detailed view (clinicians): full patterns, mechanisms, and scientific data.", "AI Refinement provides a deeper multi-agent review (consumes 1 AI analysis)."] },
    { id: "calculators", icon: "Calculator", color: "#0EA5E9", title: "11. Clinical calculators", summary: "Eleven validated clinical calculators — free for all users.", details: ["BMI, eGFR (CKD-EPI 2021), HOMA-IR, CVD risk (Framingham), Diabetes risk (FINDRISC), Vitamin D, Water intake, Menstrual cycle, Fiber, Sleep, Breast Cancer Risk (Gail/BCRAT + Bulgaria GLOBOCAN 2024).", "Plus Biological Age (Levine PhenoAge) and Mammographic Density (BI-RADS) image screening.", "All results saved and accessible to AI assistants. Downloadable as PDF."] },
    { id: "weekly", icon: "BarChart3", color: "#10B981", title: "12. Weekly AI report", summary: "Weekly AI analysis of your progress with score and recommendations.", details: ["Analyzes nutrition, DIL progress, fiber, nutrient density, and satiety.", "Shows achievements, areas for improvement, and comparison with previous week.", "Free for Core and Pro users."] },
    { id: "plans", icon: "CreditCard", color: "#F59E0B", title: "13. Plans & AI analyses", summary: "How the monthly AI analysis limit works and what each plan includes.", details: ["Each AI action (clinical review, meal plan, PDF, AI question) consumes 1 analysis from your monthly limit — flat 1:1, no hidden credit math.", "FREE (€0): diary, DIL, 11 calculators, scientific library, 1 AI question/day.", "CORE (from €9.90/mo BG · €12.90 EU · €15.90 T1): 40 AI analyses, personalized insights, DIL interpretation.", "PRO (from €19.90/mo BG · €24.90 EU · €29.90 T1): 120 AI analyses, full metabolic analysis, VAIS, Lab OCR, Digital Twin, PDF reports.", "CLINIC (€99/mo BG+EU · €119 T1 · €49 T3 PPP): 1000 AI analyses, clinical AI, protocols, white-label reports. 25 patient slots included + €4.90/extra patient.", "ENTERPRISE from €499/mo — on request.", "Regional PPP-adjusted pricing for emerging markets. Annual billing — 20% off.", "Not ready to subscribe? Unlock a single analysis for €1.99 (7 days full access)."] },
  ],
};

function StepCard({ step, isOpen, onToggle, isDark }) {
  const Icon = STEP_ICON_MAP[step.icon] || BookOpen;
  return (
    <Card className={`transition-all ${isOpen ? "shadow-md" : ""} ${isDark ? "bg-card border-border" : "border-slate-100"}`}
      data-testid={`guide-step-${step.id}`}>
      <button onClick={onToggle} className="w-full text-left p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: step.color + "12" }}>
            <Icon className="w-5 h-5" style={{ color: step.color }} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className={`text-sm font-bold mb-0.5 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{step.title}</h3>
            <p className={`text-xs ${isDark ? "text-slate-500" : "text-slate-400"}`}>{step.summary}</p>
          </div>
          {isOpen
            ? <ChevronUp className="w-4 h-4 text-slate-400 flex-shrink-0 mt-1" />
            : <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0 mt-1" />
          }
        </div>
      </button>
      {isOpen && (
        <CardContent className="px-5 pb-5 pt-0">
          <div className="ml-13 space-y-2.5 pl-[52px]">
            {step.details.map((d, i) => (
              <div key={`detail-${i}`} className="flex items-start gap-2.5">
                <div className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 text-[10px] font-bold"
                  style={{ backgroundColor: step.color + "12", color: step.color }}>
                  {i + 1}
                </div>
                <p className={`text-sm leading-relaxed ${isDark ? "text-slate-300" : "text-[#4A4A68]"}`}>{d}</p>
              </div>
            ))}
          </div>
        </CardContent>
      )}
    </Card>
  );
}

export default function GuidePage() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";
  const guide = bg ? GUIDE_BG : GUIDE_EN;
  const [openSteps, setOpenSteps] = useState(new Set(["start"]));

  const toggle = (id) => {
    setOpenSteps(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const expandAll = () => setOpenSteps(new Set(guide.steps.map(s => s.id)));
  const collapseAll = () => setOpenSteps(new Set());

  return (
    <div className={`min-h-screen ${isDark ? "bg-background text-foreground" : "bg-[#F9F9F7]"}`} data-testid="guide-page">
      <SEO
        title={bg ? "Ръководство" : "User Guide"}
        description={guide.subtitle}
        path="/guide"
      />
      <SubpageNav />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
        <SubpageBackLink label={bg ? "Начало" : "Home"} />

        {/* Header */}
        <SubpageHero
          eyebrow={bg ? "Ръководство за потребителя" : "User guide"}
          title={guide.title}
          subtitle={guide.subtitle}
          meta={bg
            ? ["Стъпка по стъпка", "Около 10 минути четене", "Актуализирано за 2026 г."]
            : ["Step by step", "About a 10-minute read", "Updated for 2026"]}
          icon={BookOpen}
          accent="#0e5c60"
          testId="guide-hero"
        />

        {/* Quick nav */}
        <div className="flex flex-wrap gap-1.5 justify-center mb-6">
          {guide.steps.map(s => {
            const Icon = STEP_ICON_MAP[s.icon] || BookOpen;
            return (
              <button key={s.id} onClick={() => { setOpenSteps(new Set([s.id])); document.querySelector(`[data-testid="guide-step-${s.id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" }); }}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-medium transition-colors ${isDark ? "bg-card hover:bg-[hsl(223,47%,15%)] text-slate-400" : "bg-white hover:bg-slate-50 text-slate-500 border border-slate-100"}`}>
                <Icon className="w-3 h-3" style={{ color: s.color }} />
                <span className="hidden sm:inline">{s.title.replace(/^\d+\.\s*/, "")}</span>
                <span className="sm:hidden">{s.title.split(".")[0]}</span>
              </button>
            );
          })}
        </div>

        {/* Expand/Collapse */}
        <div className="flex justify-end gap-2 mb-4">
          <button onClick={expandAll} className={`text-[10px] font-medium px-2 py-1 rounded ${isDark ? "text-slate-500 hover:text-slate-300" : "text-slate-400 hover:text-slate-600"}`}>
            {bg ? "Отвори всички" : "Expand all"}
          </button>
          <button onClick={collapseAll} className={`text-[10px] font-medium px-2 py-1 rounded ${isDark ? "text-slate-500 hover:text-slate-300" : "text-slate-400 hover:text-slate-600"}`}>
            {bg ? "Затвори всички" : "Collapse all"}
          </button>
        </div>

        {/* Steps */}
        <div className="space-y-3">
          {guide.steps.map(step => (
            <StepCard key={step.id} step={step} isOpen={openSteps.has(step.id)} onToggle={() => toggle(step.id)} isDark={isDark} />
          ))}
        </div>

        {/* Bottom CTA */}
        <div className={`mt-10 p-6 rounded-2xl text-center ${isDark ? "bg-card" : "bg-white border border-slate-100"}`}>
          <h3 className={`text-base font-bold mb-2 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
            {bg ? "Готови ли сте да започнете?" : "Ready to get started?"}
          </h3>
          <p className={`text-sm mb-4 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
            {bg ? "30-дневен безплатен пробен период. Без платежна карта." : "30-day free trial. No credit card required."}
          </p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Link to="/assessment">
              <button className="h-10 px-6 rounded-full bg-gradient-to-r from-[#0EA5E9] to-[#6366F1] text-white text-sm font-semibold flex items-center gap-1.5" data-testid="guide-start-cta">
                {bg ? "Започнете безплатно" : "Start free"} <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
            <Link to="/pricing">
              <button className={`h-10 px-6 rounded-full text-sm font-medium border ${isDark ? "border-slate-700 text-slate-300" : "border-slate-200 text-slate-500"}`}>
                {bg ? "Вижте плановете" : "View plans"}
              </button>
            </Link>
          </div>
        </div>
      </div>

      <SubpageFooter />
    </div>
  );
}
